import { SupabaseClient } from '@supabase/supabase-js';

export interface ResolvedReceiptStore {
  name: string;
  address: string;
  lat: number;
  lng: number;
}

// "Save Mart", "SAVEMART", "Save-Mart" -> "savemart"
const compactName = (name: string) => name.toLowerCase().replace(/[^a-z0-9]/g, '');

const DIRECTIONALS = new Set(['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw', 'north', 'south', 'east', 'west']);

// "4041 E. Ashlan, 559-224-6064" -> { number: "4041", street: "ashlan" }
export function streetKey(address: string | null | undefined): { number: string; street: string } | null {
  if (!address) return null;
  const match = address.match(/(\d{1,6})\s+([^,]+)/);
  if (!match) return null;
  const words = match[2].toLowerCase().replace(/[^a-z0-9\s]/g, ' ').split(/\s+/).filter(Boolean);
  const street = words.find((w) => !DIRECTIONALS.has(w));
  return street ? { number: match[1], street } : null;
}

/**
 * Finds the receipt's store in the store directory (public.stores): same name
 * (ignoring case, spaces and punctuation) at the same street number and street.
 * Receipts print short, noisy addresses ("4041 E. Ashlan, 559-224-6064"); the
 * directory has the full address and coordinates. Returns null when there is
 * no confident match. Shared by the basket scan (App.tsx applyReceiptMetadata)
 * and the Bulk Import worker.
 */
export async function resolveReceiptStore(
  supabase: SupabaseClient,
  storeName: string | null | undefined,
  storeAddress: string | null | undefined,
): Promise<ResolvedReceiptStore | null> {
  if (!storeName) return null;
  const key = streetKey(storeAddress);
  if (!key) return null;
  const wantedName = compactName(storeName);
  if (!wantedName) return null;

  const { data, error } = await supabase
    .from('stores')
    .select('name, address, latitude, longitude')
    .ilike('address', `${key.number} %`)
    .not('latitude', 'is', null)
    .not('longitude', 'is', null)
    .limit(50);
  if (error || !data) return null;

  const matches = data.filter((s: any) => {
    const sKey = streetKey(s.address);
    return sKey && sKey.number === key.number && sKey.street === key.street && compactName(s.name || '') === wantedName;
  });
  if (matches.length === 0) return null;

  // Duplicate directory rows exist (e.g. "SaveMart" / "Save Mart"); prefer the
  // one spelled like the receipt.
  const best = matches.find((s: any) => s.name === storeName) || matches[0];
  return { name: best.name, address: best.address, lat: Number(best.latitude), lng: Number(best.longitude) };
}

// --- Geocoding a store that isn't in the directory yet -----------------------
//
// OpenStreetMap's Nominatim: free, and its results may be stored (with
// "© OpenStreetMap contributors" attribution), unlike Mapbox's free tier or
// Google's geocoder. Its usage policy allows at most one request per second,
// so every lookup in this process goes through one queue. Each store is
// geocoded once: archiving saves the coordinates on the store, and later
// receipts from it are answered by resolveReceiptStore above.

const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';
const NOMINATIM_MIN_INTERVAL_MS = 1100;
const NOMINATIM_TIMEOUT_MS = 6000;
let nominatimQueue: Promise<unknown> = Promise.resolve();
let lastNominatimAt = 0;

async function nominatimSearch(
  query: string,
  near?: { lat: number; lng: number } | null,
): Promise<{ lat: number; lng: number; address: string } | null> {
  const run = async () => {
    const wait = lastNominatimAt + NOMINATIM_MIN_INTERVAL_MS - Date.now();
    if (wait > 0) await new Promise(r => setTimeout(r, wait));
    lastNominatimAt = Date.now();

    const params = new URLSearchParams({ q: query, format: 'jsonv2', limit: '1', addressdetails: '0' });
    if (near) {
      // Prefer results around the shopper (receipts often omit the city).
      params.set('viewbox', `${near.lng - 0.5},${near.lat + 0.5},${near.lng + 0.5},${near.lat - 0.5}`);
    }
    const headers: Record<string, string> = {};
    // Browsers send their own User-Agent/Referer; on the server we must identify ourselves.
    if (typeof window === 'undefined') headers['User-Agent'] = 'CartLens/1.0 (https://cartlens.co)';

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), NOMINATIM_TIMEOUT_MS);
    try {
      const res = await fetch(`${NOMINATIM_URL}?${params}`, { headers, signal: controller.signal });
      if (!res.ok) return null;
      const rows = await res.json();
      const hit = Array.isArray(rows) ? rows[0] : null;
      const lat = Number(hit?.lat);
      const lng = Number(hit?.lon);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      return { lat, lng, address: String(hit.display_name || '') };
    } catch {
      return null;
    } finally {
      clearTimeout(timer);
    }
  };
  const result = nominatimQueue.then(run, run);
  nominatimQueue = result.catch(() => null);
  return result;
}

// Receipt addresses come with phone numbers, store numbers and abbreviations
// ("4041 E. Ashlan, 559-224-6064"); keep only what helps a geocoder.
const cleanPrintedAddress = (address: string) =>
  address
    .replace(/\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}/g, ' ') // phone numbers
    .replace(/\bstore\s*#?\s*\d+\b/gi, ' ') // "Store #123"
    .replace(/#\s*\d+\b/g, ' ') // "#00853"
    .replace(/\s*,\s*(?=,|$)/g, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/^[\s,]+|[\s,]+$/g, '');

/**
 * Coordinates for a receipt's store that resolveReceiptStore didn't find:
 * tries the normalized address the receipt read produced ("4041 E Ashlan Ave,
 * Fresno, CA 93726"), then the printed address with the store name, then the
 * printed address alone. `near` (the shopper's last known location) breaks ties
 * when the receipt omits the city. Shared by the basket scan and Bulk Import.
 */
export async function geocodeReceiptStore(
  storeName: string | null | undefined,
  printedAddress: string | null | undefined,
  normalizedAddress?: string | null,
  near?: { lat: number; lng: number } | null,
): Promise<{ lat: number; lng: number } | null> {
  const printed = printedAddress ? cleanPrintedAddress(printedAddress) : '';
  const queries = [
    normalizedAddress?.trim(),
    storeName && printed ? `${storeName}, ${printed}` : null,
    printed || null,
  ].filter((q, i, all): q is string => !!q && q.length > 4 && all.indexOf(q) === i);

  for (const query of queries) {
    const hit = await nominatimSearch(query, near);
    if (hit) return { lat: hit.lat, lng: hit.lng };
  }
  return null;
}
